import { InteractiveElement } from 'types/interactiveWidget.types';
import { AccessibleElement, HyperLink, Widget } from 'types/widget.types';

enum IconWidgetType {
  Grid = 'Grid',
  Single = 'Single',
}

enum FillDirection {
  TopDown = 'TopDown',
  LeftRight = 'LeftRight',
}

/**
 * Embedded SVG to show custom SVG directly as icon an icon.
 *
 * This SVG might NOT support certain icon features like fill, as
 * the raw SVG is used without modifying anything right now.
 *
 * It'll always be considered as a colored icon.
 */
interface EmbeddedSVG {
  // Raw SVG as string
  svg: string;
}

interface IconWidgetData extends Widget, AccessibleElement, HyperLink, InteractiveElement {
  iconId: string;
  shapeColorOne: string;
  isMirrored: boolean;
  shapeColorTwo: string;
  shapeFill: number;
  fillDirection?: FillDirection;

  // The icon widget can be a grid (same icon repeated) or a single icon
  type?: IconWidgetType;

  // grid properties
  numberOfIcons?: number;
  gridGapPx?: number;
  gridItemWidthPx?: number;
  gridItemHeightPx?: number;

  // Metadata for Icons8 icons
  iconMetadata?: {
    // Path used to construct URL for retrieving icon from Icons8
    iconPath?: string;

    // Viewbox for the icon
    // Icons8 includes padding for all the icons so we have to manually set it
    viewBox: string;

    // Whether the icon is monochrome
    // Can't get this data directly from Icons8 API without counting towards quota so we have to manually set it
    isMono: boolean;

    // Whether to use the illustration endpoint for rendering or normal icons8 endpoint
    isIllustration?: boolean;
  } | null;

  // With SVG embedded in the widget data directly, no need to
  // fetch our own SVG downloaded in S3, using `useIcon()` from frontend/src/hooks/useIcon/useIcon.tsx
  // Having this property will not use `useIcon` call, use embedded SVG directly.
  //
  // By using this embedded SVG, our 'normal' pre-processing is disabled(removing fill, so we
  // can modify color of the icon). So an icon widget with this property CANNOT be changed in color.
  // This coloring support might be added in the future. (also percentage fill - will have no affect)
  //
  // TODO: Note that embedding of SVG in here is an EXCEPTION - should use externally fetched
  //       SVG, in more general. However, this is an experimental feature for now, NOT to be used
  //       in production yet.
  embeddedSVG?: EmbeddedSVG;
}

interface IconStyle {
  height: string;
  width: string;
  transform?: string;
  transformOrigin?: string;
}

interface AdjustSvgViewBox {
  viewBox?: string;
  maxSize: number;
}

interface Dimensions {
  width: number;
  height: number;
}

interface AdjustSvgToContainerArgs {
  viewBox: string;
  dimensions: Dimensions;
  threshold?: number;
}

export type { IconWidgetData, IconStyle, AdjustSvgViewBox, Dimensions, AdjustSvgToContainerArgs };
export { IconWidgetType, FillDirection };
